import { useSafeTheme } from '@/theme/hooks'
import { Pencil } from 'lucide-react'
import PropTypes from 'prop-types'
import { Fragment, useState } from 'react'
import FeaturePopupEditor from './FeaturePopupEditor'
import PopupShell from './PopupShell'
import {
  attributsVisibles,
  formaterCle,
  formaterValeur,
  iconeGeometrie,
} from './featurePopupFormat'

const FeaturePopup = ({
  feature,
  layerName,
  propertiesOrder,
  onClose,
  edition = null,
}) => {
  const { theme } = useSafeTheme()
  // Les hotes (clic carte, recherche) reutilisent la meme racine : l'etat suit l'entite, pas l'instance.
  const [maj, setMaj] = useState(null)
  const [enEditionDe, setEnEditionDe] = useState(null)
  const proprietes =
    maj?.feature === feature ? maj.proprietes : feature?.properties || {}
  const enEdition = enEditionDe === feature

  const changerEdition = actif => {
    setEnEditionDe(actif ? feature : null)
    edition?.onEditingChange?.(actif)
  }

  const enregistree = (
    { proprietes: nouvelles, updatedAt },
    encoreAffichee,
  ) => {
    setMaj({ feature, proprietes: nouvelles })
    if (encoreAffichee) changerEdition(false)
    edition.onSaved(updatedAt)
  }

  const attributs = attributsVisibles(
    { properties: proprietes },
    propertiesOrder,
  )

  const stylo =
    edition && !enEdition ? (
      <button
        type="button"
        onClick={() => changerEdition(true)}
        aria-label="Modifier les attributs"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-black/5 dark:hover:bg-white/10"
      >
        <Pencil size={14} />
      </button>
    ) : null

  let contenu
  if (enEdition) {
    contenu = (
      <FeaturePopupEditor
        layerId={edition.layerId}
        featureId={edition.featureId}
        onSaved={enregistree}
        onCancel={() => changerEdition(false)}
      />
    )
  } else if (attributs.length === 0) {
    contenu = (
      <p
        className="px-3 py-3 text-[11px]"
        style={{ color: theme.text.tertiary }}
      >
        Aucun attribut
      </p>
    )
  } else {
    contenu = (
      <dl className="m-0 grid max-h-72 grid-cols-[minmax(0,40%)_minmax(0,1fr)] gap-x-3 gap-y-1.5 overflow-y-auto p-3">
        {attributs.map(([cle, valeur]) => (
          <Fragment key={cle}>
            <dt
              className="break-words text-[11px] leading-5"
              style={{ color: theme.text.tertiary }}
            >
              {formaterCle(cle)}
            </dt>
            <dd className="m-0 break-words text-xs leading-5">
              {formaterValeur(valeur)}
            </dd>
          </Fragment>
        ))}
      </dl>
    )
  }

  return (
    <PopupShell
      titre={layerName?.trim() || 'Entité'}
      icone={iconeGeometrie(feature?.geometry?.type)}
      onClose={onClose}
      dataCy="feature-popup-close"
      actions={stylo}
    >
      {contenu}
    </PopupShell>
  )
}

FeaturePopup.propTypes = {
  feature: PropTypes.shape({
    geometry: PropTypes.object,
    properties: PropTypes.object,
  }).isRequired,
  layerName: PropTypes.string,
  propertiesOrder: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func.isRequired,
  edition: PropTypes.shape({
    layerId: PropTypes.number.isRequired,
    featureId: PropTypes.string.isRequired,
    onSaved: PropTypes.func.isRequired,
    onEditingChange: PropTypes.func,
  }),
}

export default FeaturePopup
